import { Sha256 } from '@aws-crypto/sha256-js';

/**
 * Calculate hash of file with specified algorithm
 *
 * Note: in unsecure contexts only SHA-256 is supported.
 *
 * Inspired by {@link https://developer.mozilla.org/en-US/docs/Web/API/Web_Crypto_API/Non-cryptographic_uses_of_subtle_crypto#hashing_a_file}
 *
 * @param file
 * @param algorithm The hashing algorithm, defaults to SHA-256
 * @returns the hash as a hexadecimal string
 */
export async function hashFile(file: File, algorithm = 'SHA-256') {
  if (window.isSecureContext) {
    return await doSecureContextHash(file, algorithm);
  } else {
    if (algorithm !== 'SHA-256') {
      throw new Error('Only SHA-256 is supported in unsecure contexts');
    }

    return await doUnsecureContextHash(file);
  }
}

async function doSecureContextHash(file: File, algorithm = 'SHA-256') {
  const arrayBuffer = await file.arrayBuffer();

  const hashAsArrayBuffer = await crypto.subtle.digest(algorithm, arrayBuffer);

  const uint8ViewOfHash = new Uint8Array(hashAsArrayBuffer);

  return convertUint8ArrayToHexString(uint8ViewOfHash);
}


async function doUnsecureContextHash(file: File) {
  const sha256 = new Sha256();

  sha256.update(await file.arrayBuffer());
  const digest = await sha256.digest();

  return convertUint8ArrayToHexString(digest);
}


function convertUint8ArrayToHexString(arr: Uint8Array): string {
  return Array.from(arr)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}
